import { useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import SpinnerSmall from "../SpinnerSmall";
import { SubscriptionDetails } from "@/generated-api";
import { formatSubscriptionDate } from "@/lib/utils";
import { useCancelSubscription } from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { CheckCircle2Icon } from "lucide-react";
import { useGetAccountLocale } from "@/hooks/useGetAccountLocale";

export default function CancelSubscriptionButton({
  details,
}: {
  details: SubscriptionDetails;
}) {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const cancelMutation = useCancelSubscription();
  const { toast } = useToast();
  const t = useTranslation();
  const locale = useGetAccountLocale();

  function handleCancelSubscription() {
    cancelMutation.mutate(details.subscription_id, {
      onSuccess: () => {
        setCancelModalOpen(false);
        toast({
          variant: "default",
          // @ts-expect-error Type 'Element' is not assignable to type
          title: (
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="h-4 w-4" />
              <span>{t.MEMBERSHIP_SETTINGS.LABELS.CANCELED_SUCCESS}</span>
            </div>
          ),
          className: "dark rounded-2xl",
        });
      },
      onError: () => {
        setCancelModalOpen(false);
        toast({
          variant: "destructive",
          title: t.MEMBERSHIP_SETTINGS.ERRORS.SUBSCRIPTION_CANCELED,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark rounded-2xl",
        });
      },
    });
  }

  return (
    <>
      <Button
        variant="link"
        className="p-0 text-red-700"
        onClick={() => setCancelModalOpen(true)}
      >
        {t.MEMBERSHIP_SETTINGS.LABELS.CANCEL_SUBSCRIPTION}
      </Button>
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="dark">
          <DialogTitle className="text-white flex flex-row items-center">
            {t.MEMBERSHIP_SETTINGS.LABELS.CANCEL_SUBSCRIPTION}
          </DialogTitle>
          <DialogDescription>
            {t.MEMBERSHIP_SETTINGS.LABELS.REMAIN_ACTIVE_UNTIL}{" "}
            {formatSubscriptionDate(details.period_end, locale.code)}
            {locale.code === "ru" ? " " : ". "}{" "}
            {t.MEMBERSHIP_SETTINGS.LABELS.NO_RENEWAL}
          </DialogDescription>
          <DialogFooter>
            <div className="w-full flex flex-row justify-between">
              <Button
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? (
                  <SpinnerSmall />
                ) : (
                  <> {t.MEMBERSHIP_SETTINGS.LABELS.CANCEL_SUBSCRIPTION}</>
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setCancelModalOpen(false)}
              >
                {t.SHARED.LABELS.BACK_BUTTON}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
